import axios from 'axios';
import { standardizeLocationName, getStateAbbreviation } from '../utils/helper';
import { updateSubscription } from './recharge';
import { cachedFetch, invalidateSharedCache } from './sharedCache';
import { apiAuthHeaders, isWildgrainApiUrl, readRechargeApiToken } from '../utils/hardeningCanary';
import { refreshRechargeSession } from '../utils/sessionRefresh';

// Base API endpoint; can be overridden via environment variable
export const BASE_API_URL = process.env.REACT_APP_BASE_API_URL || 'https://api.wildgrain.com';

// Member identity for the API: X-Member-Token (+ X-Hardening-Canary for a
// tester) on every axios call to wildgrain-api. fetch() call sites go through
// fetchWildgrainApi below for the same headers and retry.
axios.interceptors.request.use((config) => {
  if (isWildgrainApiUrl(config.url)) {
    Object.entries(apiAuthHeaders()).forEach(([name, value]) => {
      if (config.headers && typeof config.headers.set === 'function') config.headers.set(name, value);
      else config.headers = { ...(config.headers || {}), [name]: value };
    });
  }
  return config;
});

// The API answers 401 with one of these when X-Member-Token is missing or has
// lapsed (MEMBER_AUTH_MODE=enforce). The Recharge apiToken is short-lived and
// only the SDK's own 401s trigger a refresh, so mint a fresh session and retry
// once rather than failing the call. Other 401s (admin, magic-link) pass through.
const MEMBER_SESSION_ERRORS = ['Member session required', 'Invalid member session'];
const isMemberSessionRejection = (status, body) =>
  status === 401 && MEMBER_SESSION_ERRORS.includes(body && body.error);

// A burst of parallel calls can all come back 401 on the same stale token; once
// one of them has refreshed, the rest just retry with the new token.
const ensureFreshMemberSession = async (sentToken) => {
  const current = readRechargeApiToken();
  if (current && current !== sentToken) return true;
  return Boolean(await refreshRechargeSession());
};

axios.interceptors.response.use(undefined, async (error) => {
  const config = error && error.config;
  const response = error && error.response;
  if (
    !config ||
    config._memberSessionRetried ||
    !isWildgrainApiUrl(config.url) ||
    !response ||
    !isMemberSessionRejection(response.status, response.data)
  ) {
    throw error;
  }
  const sentToken = config.headers && (typeof config.headers.get === 'function'
    ? config.headers.get('X-Member-Token')
    : config.headers['X-Member-Token']);
  if (!(await ensureFreshMemberSession(sentToken || ''))) throw error;
  config._memberSessionRetried = true;
  return axios(config); // the request interceptor re-reads the fresh token
});

// fetch() equivalent of the two interceptors above, for call sites that need
// fetch semantics (non-throwing on HTTP errors).
export const fetchWildgrainApi = async (url, init = {}) => {
  const send = () => fetch(url, { ...init, headers: { ...(init.headers || {}), ...apiAuthHeaders() } });
  const sentToken = readRechargeApiToken();
  const response = await send();
  if (response.status !== 401) return response;
  let body = null;
  try {
    body = await response.clone().json();
  } catch {
    return response;
  }
  if (!isMemberSessionRejection(response.status, body)) return response;
  return (await ensureFreshMemberSession(sentToken)) ? send() : response;
};

// Server-side login-token endpoints (wildgrain-api auth/). Same response shape
// as impersonateCustomer — multipass_url, storefront_customer_access_token,
// token_expires_at, customer — but no admin credential is involved.
export const exchangeMagicLink = async ({ email, customerId, customerHash }) => {
  try {
    const { data } = await axios.post(`${BASE_API_URL}/api/auth/magic-link-exchange`, { email, customerId, customerHash });
    return data;
  } catch (error) {
    console.error('Error exchanging magic link:', error?.response?.data || error.message);
    return null;
  }
};

export const mintStorefrontToken = async (rechargeSessionToken) => {
  try {
    const { data } = await axios.post(`${BASE_API_URL}/api/auth/storefront-token`, { rechargeSessionToken });
    return data;
  } catch (error) {
    console.error('Error minting storefront token:', error?.response?.data || error.message);
    return null;
  }
};

// Shared-cache TTLs. Box/bundle content and delivery data barely change
// within a session, so short TTLs cut the repeat fetches without staleness
// risk — and cachedFetch also dedupes concurrent requests for the same key.
const BOX_CONTENT_TTL_MS = 5 * 60_000;
const ESTIMATE_TTL_MS = 10 * 60_000;
const TRACKING_TTL_MS = 10 * 60_000;

// ---------------------------------------------------------------------------
// Redesign A/B experiment – event stamping
// ---------------------------------------------------------------------------
// Reads the member's current experiment assignment straight from localStorage
// (written by src/services/redesignExperiment.js) so it can be attached to
// every order/box Klaviyo event WITHOUT importing that module (avoids a
// circular dependency: redesignExperiment.js imports from this file).
//
// Stamping bucket + active_version onto each order event is what makes the
// downstream before/after AOV & conversion analysis possible — every order
// then records which cohort the member was in and which version they were
// actually viewing when the order happened.
const readRedesignContext = () => {
  try {
    const bucket = localStorage.getItem('redesign_bucket');
    // The ?design= pin shadows the cached assignment (redesignExperiment.js's
    // getActiveVersion), so read it the same way round or events would record a
    // version the member isn't looking at.
    const activeVersion = localStorage.getItem('redesign_design_override')
      || localStorage.getItem('redesign_active_version');
    const ctx = {};
    if (bucket) ctx.redesign_bucket = bucket;
    if (activeVersion) ctx.redesign_active_version = activeVersion;
    return ctx;
  } catch {
    return {};
  }
};

// Portal action log (wildgrain-api `user_events`, read by the Website Insights
// dashboard). The receiving endpoint has existed since Aug 2026 but nothing
// ever called it, so cancellation-flow save rates read as zero — see
// cartInsights/service.js getCancellationSaveRate.
//
// Deliberately fire-and-forget: analytics must never block or fail a retention
// action, so this swallows its own errors and the callers don't await it.
export const recordUserEvent = ({
  email,
  subscriptionId,
  eventName,
  step,
  action,
  properties,
} = {}) => {
  const ctx = readRedesignContext();
  return axios
    .post(`${BASE_API_URL}/api/user-events`, {
      email: email || null,
      subscriptionId: subscriptionId ? String(subscriptionId) : null,
      eventName,
      step,
      action: action || null,
      // The API validates portalVersion against ['new','old'] and 400s on
      // anything else, so send null rather than an unrecognised value.
      portalVersion: ctx.redesign_active_version === 'new' || ctx.redesign_active_version === 'old'
        ? ctx.redesign_active_version
        : null,
      redesignBucket: ctx.redesign_bucket || null,
      properties: properties || null,
    })
    .catch((err) => {
      console.warn('Failed to record user event', eventName, step, err?.message || err);
    });
};

// withStage: the estimate is for this order as it stands (OrderCard), so the API
// may use Grip's status for a range / firm date. Off for the modals, which ask
// about a hypothetical new order using the latest order as a reference.
export const getOrderEstimateDate = async (shopifyOrderId, dateDescription, { withStage = false } = {}) => {
  if (!shopifyOrderId) {
    return null;
  }

  try {
    return await cachedFetch(
      ['orderEstimate', String(shopifyOrderId), dateDescription, withStage ? 'stage' : ''],
      async () => {
        try {
          const url = `${BASE_API_URL}/api/order/delivery/${shopifyOrderId}/${dateDescription}${withStage ? '?stage=1' : ''}`;
          const response = await axios.get(url);

          const orderEstimate = response.data;
          if (orderEstimate && orderEstimate.error) {
            console.error('Error in order estimate response:', orderEstimate.error);
            return null;
          }
          return orderEstimate ?? null;
        } catch (error) {
          // A 404 is a definitive "no estimate" — cache the null. Anything
          // else is transient, so rethrow to keep it out of the cache.
          if (error.response && error.response.status === 404) return null;
          throw error;
        }
      },
      ESTIMATE_TTL_MS
    );
  } catch (error) {
    console.error('Error fetching order estimate:', error);
    return null;
  }
};

export const getDeliveryTracking = async (shopifyOrderId, orderName) => {
  if (!shopifyOrderId) return null;

  const cleanId = String(shopifyOrderId).replace('gid://shopify/Order/', '').split('?')[0];

  try {
    return await cachedFetch(
      ['deliveryTracking', cleanId],
      async () => {
        try {
          let url = `${BASE_API_URL}/api/grip/tracking/${cleanId}`;
          if (orderName) {
            url += `?orderName=${encodeURIComponent(orderName)}`;
          }
          const response = await axios.get(url);
          return response.data ?? null;
        } catch (error) {
          if (error.response && error.response.status === 404) return null;
          throw error;
        }
      },
      TRACKING_TTL_MS
    );
  } catch (error) {
    console.error('Error fetching delivery tracking:', error);
    return null;
  }
};

export const getFulfillmentLocationNameByZip = async (zip) => {
  try {
    let trimmedZip = zip.substring(0, 5);
    let url = `${BASE_API_URL}/api/grip/fc/${trimmedZip}`;
    const response = await axios.get(url);
    return response.data.fulfillment_location_name;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getFulfillmentLocation = async (subscription) => {
  let fulfillmentLocation = null;
  let zip = null;

  let address = subscription.include.address;
  if (address && address.zip) {
    zip = address.zip;
  } else {
    if (subscription.addresses && subscription.addresses.length > 0) {
      zip = subscription.addresses[0].zip;
    }
  }

  if (subscription.properties && subscription.properties.find((property) => property.name === 'fulfillment_location')) {
    fulfillmentLocation = subscription.properties.find((property) => property.name === 'fulfillment_location').value;
  }

  // Check for deprecated fulfillment locations (case-insensitive).
  // FNS was replaced by Grip_TX (Denton) in Aug 2026 — re-resolving via zip
  // returns 'grip_tx' and persists it onto the subscription, so tag filtering
  // uses the live YES_/NO_GRIP_TX tags instead of the frozen FNS ones.
  const deprecatedLocations = ['ICECUBE', 'BURRIS_MD', 'JAX', 'NEESVIGS', 'SIERRA', 'FNS'];
  if (fulfillmentLocation && deprecatedLocations.includes(fulfillmentLocation.toUpperCase())) {
    fulfillmentLocation = null;
  }

  if (!fulfillmentLocation && zip !== null) {
    const response = await getFulfillmentLocationNameByZip(zip).catch((error) => {
      console.error('Error fetching fulfillment location:', error);
      return null;
    });

    if (response) {
      fulfillmentLocation = response;

      // Persist the resolved location onto the subscription so the next load
      // skips the zip lookup. Fire-and-forget: the write is a persistence
      // optimization and must not add a round-trip to this read path.
      try {
        const existingProperties = subscription.properties || [];

        const filteredProperties = existingProperties.filter(
          prop => prop.name !== 'fulfillment_location'
        );

        const newProperties = [
          ...filteredProperties,
          {
            name: 'fulfillment_location',
            value: fulfillmentLocation,
          }
        ];

        updateSubscription(subscription.id, { properties: newProperties }).catch((error) => {
          console.error('Failed to update subscription properties:', error);
        });
      } catch (error) {
        console.error('Failed to update subscription properties:', error);
      }

      fulfillmentLocation = standardizeLocationName(fulfillmentLocation);
    }
  }

  return fulfillmentLocation;
};

export const getBoxContent = async (box_type, fulfillmentLocation, month, sku, recharge_substitutions, boxNumber = null) => {
  try {
    return await cachedFetch(
      ['boxContent', box_type, fulfillmentLocation, month, sku, recharge_substitutions, boxNumber],
      async () => {
        let url = `${BASE_API_URL}/api/box/portal?location=${fulfillmentLocation}&sku=${sku}&month=${month}&type=${box_type}&substitutions=${encodeURIComponent(
          JSON.stringify(recharge_substitutions)
        )}&html=false`;
        // WGP_ bundle boxes rotate; pass the current box number for backend resolution.
        if (boxNumber != null) url += `&boxNumber=${boxNumber}`;

        const response = await axios.get(url);
        return response.data;
      },
      BOX_CONTENT_TTL_MS
    );
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// Approved recipes (wildgrain-api recipes/, reviewed in the admin Recipes
// tool) for the Baking Tips page. Empty list on failure — the page still works.
export const getApprovedRecipes = async () => {
  try {
    return await cachedFetch(
      ['approvedRecipes'],
      async () => {
        const { data } = await axios.get(`${BASE_API_URL}/api/recipes/public`);
        return Array.isArray(data?.recipes) ? data.recipes : [];
      },
      BOX_CONTENT_TTL_MS
    );
  } catch (error) {
    console.error('Error loading recipes:', error?.response?.data || error.message);
    return [];
  }
};

export const getSubscriptionBundleContent = async (bundleSku, boxNumber, chargeDate = null) => {
  try {
    return await cachedFetch(
      ['bundleContent', bundleSku, boxNumber, chargeDate],
      async () => {
        let url = `https://api.wildgrain.com/api/bundle/storefront/${encodeURIComponent(bundleSku)}?box_number=${boxNumber}`;
        // Monthly bundles resolve their curation from this date server-side, so the
        // preview matches the month that will actually ship.
        if (chargeDate) url += `&date=${encodeURIComponent(chargeDate)}`;
        const response = await axios.get(url);
        return response.data;
      },
      BOX_CONTENT_TTL_MS
    );
  } catch (error) {
    console.error('Error fetching subscription bundle content:', error);
    throw error;
  }
};

// ---------------------------------------------------------------------------
// Charge reminder lead time (Account "Charge Reminder" card). customerId is the
// Recharge customer id. The API sends the pre-charge reminder email/SMS this
// many days before each charge (2–10, default 4).
// ---------------------------------------------------------------------------

export const getChargeReminderDays = async (customerId) => {
  if (!customerId) return null;
  try {
    const res = await axios.get(`${BASE_API_URL}/api/charge-reminder/${encodeURIComponent(customerId)}`);
    return res.data;
  } catch (err) {
    console.error('Error loading charge reminder setting:', err);
    return null;
  }
};

export const setChargeReminderDays = async (customerId, days) => {
  const res = await axios.put(`${BASE_API_URL}/api/charge-reminder/${encodeURIComponent(customerId)}`, { days });
  return res.data;
};

// ---------------------------------------------------------------------------
// Vacation schedule (Member Account "Vacation Schedule" feature)
// ---------------------------------------------------------------------------

const vacationUrl = (path = '') => `${BASE_API_URL}/api/vacation${path}`;

export const listVacations = async (customerId) => {
  if (!customerId) return [];
  try {
    const res = await axios.get(vacationUrl(`?customer_id=${encodeURIComponent(customerId)}`));
    return res.data?.vacations || [];
  } catch (err) {
    console.error('Error listing vacations:', err);
    return [];
  }
};

export const createVacation = async (payload) => {
  const res = await axios.post(vacationUrl(''), payload);
  return res.data?.vacation;
};

export const updateVacation = async (id, payload) => {
  const res = await axios.put(vacationUrl(`/${id}`), payload);
  return res.data?.vacation;
};

export const deleteVacation = async (id) => {
  const res = await axios.delete(vacationUrl(`/${id}`));
  return res.data?.vacation;
};

// Add a helper to process a queued charge immediately via Wildgrain backend
export const processCharge = async (chargeId, customerId, isAutoChargeDisabled = false) => {
  try {
    const url = 'https://api.wildgrain.com/api/recharge/process';
    const payload = { 
      chargeId, 
      customerId,
      isNewPortal: true,
      isAutoChargeDisabled
    };

    const response = await fetchWildgrainApi(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // The order changes the charge list and bumps the subscription's next
    // charge date. Drop the cached copies (30s TTL) so the refetch that
    // follows can't be served the pre-order state — that stale re-render
    // looked like "the page didn't update" until a manual reload.
    invalidateSharedCache('charges');
    invalidateSharedCache('subscriptions');
    invalidateSharedCache('onetimes');
    invalidateSharedCache('customerOrders');

    // Check if response has content before trying to parse as JSON
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    } else {
      // For non-JSON responses (like "OK"), return the text or a success indicator
      const text = await response.text();
      return { success: true, message: text || 'Success' };
    }
  } catch (error) {
    console.error('Error processing charge:', error);
    throw error;
  }
};

// Ask the backend to immediately reprocess a charge stuck in Recharge's
// error/dunning state (used right after a member saves a new primary card, so
// they don't wait up to 24h for Recharge's next scheduled retry).
// Resolves to { retried, charge_status, error? }.
export const retryErrorCharge = async (chargeId, customerId) => {
  const response = await fetchWildgrainApi(`${BASE_API_URL}/api/recharge/retry-charge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ chargeId, customerId }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return await response.json();
};

// Ask the backend to clear a STALE failed charge — one the member has since
// paid past on the same address, so it's a dead charge locking their box
// rather than a card problem. The backend re-verifies that and reschedules the
// charge onto the box subscription's date (2101 for on-demand members).
// Resolves to { cleared, charge_status?, target_date?, reason? }.
export const clearStaleFailedCharge = async (chargeId, customerId) => {
  const response = await axios.post(`${BASE_API_URL}/api/recharge/clear-stale-charge`, { chargeId, customerId });
  return response.data;
};

// Send a customer payment update email
export const sendCustomerPaymentUpdateEmail = async (customerPaymentMethodId, { customerId, email } = {}) => {
  if (!customerPaymentMethodId) {
    throw new Error('customerPaymentMethodId is required to send payment update email');
  }

  // Ensure we always pass a valid Shopify Global ID (gid://shopify/CustomerPaymentMethod/123...) to the backend.
  const formatAsGid = (id) => {
    const str = String(id);
    // Already a properly-formatted GID
    if (str.startsWith('gid://')) return str;
    // If the value is a bare number (or numeric string) wrap it in the expected GID format
    if (/^\d+$/.test(str)) {
      return `gid://shopify/CustomerPaymentMethod/${str}`;
    }
    // Otherwise return it unchanged (processor tokens, etc.)
    return str;
  };

  try {
    const url = `${BASE_API_URL}/api/payment-method/send-update-email`;

    const payload = { customerPaymentMethodId: formatAsGid(customerPaymentMethodId) };
    if (customerId) payload.customerId = customerId;
    if (email) payload.email = email;

    const response = await axios.post(url, payload);
    return response.data;
  } catch (error) {
    console.error('Error sending customer payment update email:', error);

    // Surface a readable message (if provided by backend) so the UI can display it.
    const friendlyMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error.message ||
      'Unable to send update email.';

    // Recreate error object so message is correctly set when caught downstream
    throw new Error(friendlyMessage);
  }
};

export const updateDryIce = async (customerId) => {
  try {
    const url = `${BASE_API_URL}/api/retention/dryice/${customerId}`;
    const response = await axios.put(url);
    return response.data;
  } catch (error) {
    console.error('Error updating dry ice:', error);
    throw error;
  }
};

export const updateCarrier = async (customerId) => {
  try {
    const url = `${BASE_API_URL}/api/retention/carrier/${customerId}`;
    const response = await axios.put(url);
    return response.data;
  } catch (error) {
    console.error('Error updating carrier:', error);
    throw error;
  }
};

// In-memory cache for getRewards to prevent duplicate API calls
const rewardsCache = new Map();
const rewardsPendingRequests = new Map();
const REWARDS_CACHE_TTL = 300000; // 5 minutes

export const getRewards = async (email, monthDate) => {
  try {
    const formattedDate = monthDate.slice(0, 7) + '-01';
    const cacheKey = `${email}:${formattedDate}`;
    
    // Check if we have a valid cached result
    const cached = rewardsCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < REWARDS_CACHE_TTL) {
      return cached.data;
    }
    
    // Check if there's already a pending request for this key (dedupe parallel calls)
    if (rewardsPendingRequests.has(cacheKey)) {
      return rewardsPendingRequests.get(cacheKey);
    }
    
    const url = `${BASE_API_URL}/api/rewards/${formattedDate}/${email}`;
    const requestPromise = axios.get(url).then(response => {
      // Cache the result
      rewardsCache.set(cacheKey, { data: response.data, timestamp: Date.now() });
      // Remove from pending
      rewardsPendingRequests.delete(cacheKey);
      return response.data;
    }).catch(error => {
      // Remove from pending on error
      rewardsPendingRequests.delete(cacheKey);
      throw error;
    });
    
    // Track this request as pending
    rewardsPendingRequests.set(cacheKey, requestPromise);
    
    return requestPromise;
  } catch (error) {
    console.error('Error fetching rewards:', error);
    throw error;
  }
};

// Utility to invalidate rewards cache (call after updating rewards)
export const invalidateRewardsCache = (email, monthDate) => {
  if (email && monthDate) {
    const formattedDate = monthDate.slice(0, 7) + '-01';
    rewardsCache.delete(`${email}:${formattedDate}`);
  } else {
    // Clear entire cache if no specific key provided
    rewardsCache.clear();
  }
};

// ---------------------------
// Flash Sale
// ---------------------------

// Cache for flash sale to avoid duplicate requests
const flashSaleCache = new Map();
const FLASH_SALE_CACHE_TTL = 60000; // 1 minute

/**
 * Get active flash sale for the given email if eligible
 * @param {string} email - Customer email
 * @returns {Object|null} Flash sale data or null if not eligible
 */
export const getFlashSale = async (email) => {
  if (!email) return null;
  
  try {
    const cacheKey = email.toLowerCase();
    
    // Check cache first
    const cached = flashSaleCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < FLASH_SALE_CACHE_TTL) {
      return cached.data;
    }
    
    const url = `${BASE_API_URL}/api/flash-sale/${encodeURIComponent(email)}`;
    const response = await axios.get(url);
    
    // Cache the result
    flashSaleCache.set(cacheKey, {
      data: response.data,
      timestamp: Date.now()
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching flash sale:', error);
    return null;
  }
};

export const updateReward = async (email, monthDate, rewardId) => {
  try {
    const formattedDate = monthDate.slice(0, 7) + '-01';
    const url = `${BASE_API_URL}/api/rewards`;

    const response = await axios.post(url, {
      email,
      reward_id: rewardId,
      month: formattedDate
    });

    // Invalidate cache so next fetch gets fresh data
    invalidateRewardsCache(email, monthDate);

    return response.data;
  } catch (error) {
    console.error('Error updating reward:', error);
    throw error;
  }
};

export const getShopifyOrder = async (orderId) => {
  try {
    const url = `${BASE_API_URL}/api/shopify/order/${orderId}`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching your order:', error);
    throw error;
  }
};

// ---------------------------
// Admin – customer impersonation
// ---------------------------

/**
 * Trigger an admin impersonation session.
 *
 * @param {Object} params - The parameters for impersonation
 * @param {string} [params.customerId] - The customer's ID
 * @param {string} [params.customerEmail] - The customer's email
 * @param {string} [params.customerFirstName] - The customer's first name
 * @param {string} [params.customerLastName] - The customer's last name
 * @param {string} [params.adminUsername] - The admin's username
 * @param {string} [params.adminPassword] - The admin's password
 * @returns {Promise<{
 *   multipass_url: string,
 *   multipass_token: string,
 *   storefront_customer_access_token: string,
 *   token_expires_at: string,
 *   customer: {
 *     email: string,
 *     name: string
 *   }
 * }>} The impersonation response containing tokens and customer info
 * @throws {Error} If neither customerId nor customerEmail is provided
 * @throws {Error} If admin credentials are missing
 */
export const impersonateCustomer = async ({ customerId, customerEmail, customerFirstName, customerLastName, adminUsername, adminPassword }) => {
  if (!customerId && !customerEmail) {
    throw new Error('Either customerId or customerEmail must be supplied');
  }

  try {
    const payload = {
      customer: {
        email: customerEmail,
        id: customerId,
        firstName: customerFirstName,
        lastName: customerLastName
      }
    };

    // Admin credentials are typed in at runtime (AdminImpersonationForm); they
    // are never built into the bundle.
    const username = adminUsername;
    const password = adminPassword;

    if (!username || !password) {
      throw new Error('Missing admin credentials. Please provide username/password.');
    }

    let authKey;

    if (typeof btoa === 'function') {
      authKey = btoa(`${username}:${password}`);
    } else if (typeof Buffer !== 'undefined') {
      authKey = Buffer.from(`${username}:${password}`).toString('base64');
    } else {
      throw new Error('Unable to encode admin credentials to base64');
    }

    const response = await axios.post(`${BASE_API_URL}/api/admin/impersonate`, payload, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Basic ${authKey}`,
      },
    });

    // The response will include:
    // - multipass_url
    // - multipass_token
    // - storefront_customer_access_token
    // - token_expires_at
    // - customer object with id, email, and name
    return response.data;
  } catch (error) {
    console.error('Error impersonating customer:', error);
    throw error;
  }
};

// Add helper to fetch bundle configuration data for a specific bundle (Box Builder)
export const getBundleConfigData = async (bundleId) => {
  try {
    const url = `${BASE_API_URL}/api/recharge/bundle-data/${bundleId}`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching bundle configuration data:', error);
    throw error;
  }
};

// ---------------------------
// Referral links
// ---------------------------

export const getReferralLink = async (email) => {
  if (!email) throw new Error('Email is required to fetch referral link');
  try {
    const url = `${BASE_API_URL}/api/referrals/referral?email=${encodeURIComponent(email)}`;
    const { data } = await axios.get(url);
    return data;
  } catch (err) {
    // Simply re-throw so caller can handle 404 vs other errors
    throw err;
  }
};

export const createReferralLink = async (email, first_name = '', last_name = '') => {
  if (!email) throw new Error('Email is required to create referral link');
  try {
    const url = `${BASE_API_URL}/api/referrals/referral`;
    const payload = { email };
    if (first_name) payload.first_name = first_name;
    if (last_name) payload.last_name = last_name;
    const { data } = await axios.post(url, payload);
    return data;
  } catch (err) {
    console.error('Error creating referral link:', err);
    throw err;
  }
};

export const getReferralCampaigns = async () => {
  const { data } = await axios.get(`${BASE_API_URL}/api/referrals/campaigns`);
  return data;
};

export const getFriendOfferCampaigns = async () => {
  const { data } = await axios.get(`${BASE_API_URL}/api/friend-offer/campaigns`);
  return data;
};

// Fires the "Reward At Risk" Klaviyo event: the member's selected monthly
// reward will be forfeited because no charge lands this month. Lifecycle
// triggers email/SMS off it (see RewardProgressCard — sent once per month).
export const trackRewardAtRisk = async (email, params = {}) => {
  const { data } = await axios.post(`${BASE_API_URL}/api/klaviyo`, {
    emails: [email],
    event: 'Reward At Risk',
    params,
  });
  return data;
};

export const sendFreeBoxInvites = async (emails, shareUrl, advocateEmail, advocateFirstName, advocateLastName) => {
  const { data } = await axios.post(`${BASE_API_URL}/api/klaviyo`, {
    emails,
    event: 'Free Box Invite',
    params: {
      referral_url: shareUrl,
      advocate_email: advocateEmail,
      advocate_first_name: advocateFirstName,
      advocate_last_name: advocateLastName,
      campaign_name: 'Free First Box',
    },
  });
  return data;
};

/**
 * Send a member-initiated contact message (e.g. from the cancel-flow
 * "Connect with a Baking Expert" CTA) to the Wildgrain backend, which
 * forwards it to support@wildgrain.com via SendGrid.
 *
 * The SendGrid API key stays server-side — we never call SendGrid from the
 * browser. The backend route `/api/support/contact` composes the email with
 * the member as Reply-To and as the friendly `from.name`, so the support
 * inbox shows the member as the sender and "Reply" goes back to them.
 *
 * Backend contract:
 *   POST ${BASE_API_URL}/api/support/contact
 *     body: { email, name, subject, message, reason, subscriptionId, customerId, source }
 *   -> 200 { ok: true, messageId }                 (delivered to support@wildgrain.com)
 *      400 { ok: false, error: 'validation', details }
 *      429 { ok: false, error: 'rate_limited' }
 *      502 { ok: false, error: 'upstream', details } (SendGrid rejected)
 *      500 { ok: false, error: 'misconfigured' | 'internal' }
 *
 * @param {Object} payload
 * @param {string} payload.email        – Member's email (required, used as Reply-To)
 * @param {string} [payload.name]       – Member's name
 * @param {string} payload.subject      – Email subject (required)
 * @param {string} payload.message      – Message body (required)
 * @param {string} [payload.reason]     – Originating cancel reason (if any)
 * @param {string|number} [payload.subscriptionId]
 * @param {string|number} [payload.customerId]
 * @returns {Promise<Object>} Backend response (`{ ok, messageId }` on success)
 */
export const sendMemberContactMessage = async ({
  email,
  name = '',
  subject,
  message,
  reason = '',
  subscriptionId = null,
  customerId = null,
} = {}) => {
  if (!email) throw new Error('email is required to send contact message');
  if (!message) throw new Error('message is required to send contact message');

  const { data } = await axios.post(`${BASE_API_URL}/api/support/contact`, {
    email,
    name,
    subject,
    message,
    reason,
    subscriptionId,
    customerId,
    source: 'cancellation-flow',
  });
  return data;
};

// Send referral invite emails via Wildgrain notify endpoint
export const sendReferralInvites = async (
  emails = [],
  referralUrl,
  advocateEmail = '',
  advocateFirstName = '',
  advocateLastName = '',
  campaignData = {}
) => {
  if (!Array.isArray(emails) || emails.length === 0) {
    throw new Error('At least one email is required to send referral invites');
  }

  try {
    const url = `${BASE_API_URL}/api/klaviyo`;

    const payload = {
      emails,
      event: 'Referral Invite',
      params: {
        referral_url: referralUrl,
        advocate_email: advocateEmail,
        advocate_first_name: advocateFirstName,
        advocate_last_name: advocateLastName,
        ...campaignData,
      },
    };

    const { data } = await axios.post(url, payload);
    return data;
  } catch (error) {
    console.error('Error sending referral invite emails:', error);
    throw error;
  }
};

// ---------------------------
// Klaviyo Magic-link helpers
// ---------------------------

/**
 * Retrieve a customer object by their email address. Primarily used by the
 * Klaviyo magic-link flow to verify the incoming request parameters.
 *
 * @param {string} email – The customer's plain-text email address
 * @returns {Promise<Object|null>} – The customer record or null when not found
 */
export const getCustomerByEmail = async (email) => {
  if (!email) throw new Error('Email is required');
  try {
    const url = `${BASE_API_URL}/api/recharge/customer/${encodeURIComponent(email)}`;
    const { data } = await axios.get(url);
    return data;
  } catch (error) {
    console.error('Error fetching customer by email:', error);
    return null; // Swallow to allow graceful fallback to /login
  }
};

// Track "Order Now" events in Klaviyo
export const trackOrderNow = async (email, subscriptionId, chargeId) => {
  if (!email) {
    console.error('trackOrderNow: email is required');
    return null;
  }

  try {
    const url = `${BASE_API_URL}/api/klaviyo`;

    const payload = {
      emails: [email],
      event: 'Order Now',
      params: {
        subscription_id: subscriptionId,
        charge_id: chargeId,
        ...readRedesignContext(),
      },
    };

    const { data } = await axios.post(url, payload);
    return data;
  } catch (error) {
    console.error('Error sending Klaviyo Order Now event:', error);
    return null;
  }
};

// Add helper to track reschedule events in Klaviyo
export const trackRescheduledEvent = async (email, subscriptionId, newDate, rescheduledOutOfCurrentMonth = false, extraParams = {}) => {
  if (!email) {
    console.error('trackRescheduledEvent: email is required');
    return null;
  }

  try {
    const url = `${BASE_API_URL}/api/klaviyo`;

    const payload = {
      emails: [email],
      event: 'Box Rescheduled',
      params: {
        subscription_id: subscriptionId,
        new_date: newDate,
        rescheduled_out_of_current_month: rescheduledOutOfCurrentMonth,
        // Redesign stamp + push depth make the reschedule-loop experiment
        // measurable per portal variant (order-now / box-save events already
        // carry the same context).
        ...extraParams,
        ...readRedesignContext(),
      },
    };

    const { data } = await axios.post(url, payload);
    return data;
  } catch (error) {
    console.error('Error sending Klaviyo reschedule event:', error);
    return null;
  }
};

// Add helper to track box selection save events in Klaviyo
export const trackBoxSelectionSaved = async (email, subscriptionId, products = [], nextChargeDate = null) => {
  if (!email) {
    console.error('trackBoxSelectionSaved: email is required');
    return null;
  }

  try {
    const url = `${BASE_API_URL}/api/klaviyo`;

    const payload = {
      emails: [email],
      event: 'Box Selection Saved',
      params: {
        subscription_id: subscriptionId,
        products: products.map((p) => ({
          id: p.id,
          title: p.title,
          quantity: p.quantity,
          is_addon: !!p.isAddon,
          is_free: !!p.isFree,
        })),
        productFormatted: `<ul>${products.map((p) => `<li>${p.quantity}x ${p.title}</li>`).join('')}</ul>`,
        next_charge_date: nextChargeDate,
        ...readRedesignContext(),
      },
    };

    const { data } = await axios.post(url, payload);
    return data;
  } catch (error) {
    console.error('Error sending Klaviyo box selection event:', error);
    return null;
  }
};

// Add helper to tag Shopify customer upon first login to new portal
export const trackNewPortalLogin = async (customerId) => {
  if (!customerId) {
    console.warn('trackNewPortalLogin: customerId is required');
    return null;
  }

  try {
    const url = `${BASE_API_URL}/api/shopify/customer/tag`;
    // Build month-based tag, e.g., "NewPortal_07"
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const tag = `NewPortal_${month}`;

    const payload = { id: customerId, tag: tag };

    const { data } = await axios.post(url, payload);
    return data;
  } catch (error) {
    console.error('Error tagging customer with NewPortal month tag:', error);
    return null; // Swallow so login flow proceeds even if tagging fails
  }
};

/**
 * Tag a Shopify customer when they accept the legacy box upgrade
 * @param {string} customerId - The Shopify customer ID
 * @returns {Promise<Object|null>} Response data or null on error
 */
export const tagLegacyUpgradeAccepted = async (customerId) => {
  if (!customerId) {
    console.warn('tagLegacyUpgradeAccepted: customerId is required');
    return null;
  }

  try {
    const url = `${BASE_API_URL}/api/shopify/customer/tag`;
    const payload = { id: customerId, tag: 'LEGACY_UPGRADE_ACCEPTED' };

    const { data } = await axios.post(url, payload);
    console.log('Successfully tagged customer with LEGACY_UPGRADE_ACCEPTED');
    return data;
  } catch (error) {
    console.error('Error tagging customer with LEGACY_UPGRADE_ACCEPTED:', error);
    return null; // Don't block the migration flow if tagging fails
  }
};

export const trackLegacyBoxUpgrade = async (email, properties = {}) => {
  if (!email) {
    console.error('trackLegacyBoxUpgrade: email is required');
    return null;
  }

  try {
    const url = `${BASE_API_URL}/api/klaviyo`;

    const payload = {
      emails: [email],
      event: 'Legacy Box Upgrade',
      params: properties,
    };

    const { data } = await axios.post(url, payload);
    return data;
  } catch (error) {
    console.error('Error sending Klaviyo Legacy Box Upgrade event:', error);
    return null;
  }
};

// Track portal visit events in Klaviyo
export const trackPortalVisit = async (email, method = '') => {
  if (!email) {
    console.error('trackPortalVisit: email is required');
    return null;
  }

  try {
    const url = `${BASE_API_URL}/api/klaviyo`;

    const payload = {
      emails: [email],
      event: 'Portal Visit',
      // Only include params when a method is provided to keep the payload small
      params: method ? { method } : {},
    };

    const { data } = await axios.post(url, payload);
    return data;
  } catch (error) {
    console.error('Error sending Klaviyo portal visit event:', error);
    return null;
  }
};

// Update Klaviyo profile with current nonprofit selection from Beam
export const updateKlaviyoNonprofitSelection = async (email, nonprofitName) => {
  if (!email) {
    console.error('updateKlaviyoNonprofitSelection: email is required');
    return null;
  }

  if (!nonprofitName) {
    console.error('updateKlaviyoNonprofitSelection: nonprofitName is required');
    return null;
  }

  try {
    const url = `${BASE_API_URL}/api/klaviyo/profile`;

    const payload = {
      email,
      properties: {
        currentNonprofitSelection: nonprofitName,
      },
    };

    const { data } = await axios.post(url, payload);
    return data;
  } catch (error) {
    console.error('Error updating Klaviyo profile nonprofit selection:', error);
    return null;
  }
};

// ---------------------------------------------------------------------------
// Redesign A/B experiment – assignment, switch & feedback tracking
// ---------------------------------------------------------------------------

/**
 * Persist the member's experiment assignment onto their Klaviyo profile so it
 * is durable and queryable server-side (localStorage is only the client cache).
 * `redesign_bucket` is the sticky cohort (control|test); `redesign_active_version`
 * is what they are currently viewing (old|new) and updates on every switch.
 */
/**
 * Read a member's redesign assignment back from their Klaviyo profile so it can
 * follow them across devices at login. Never throws — returns null on any
 * failure so callers fall back to the local/created-at assignment.
 *
 * @param {string} email
 * @returns {Promise<{bucket: string|null, activeVersion: string|null}|null>}
 */
export const getRedesignProfile = async (email) => {
  if (!email) return null;
  try {
    const { data } = await axios.get(
      `${BASE_API_URL}/api/klaviyo/profile/${encodeURIComponent(email)}`
    );
    return {
      bucket: data?.redesign_bucket || null,
      activeVersion: data?.redesign_active_version || null,
    };
  } catch (error) {
    console.error('Error fetching Klaviyo redesign profile:', error);
    return null;
  }
};

export const setRedesignProfile = async (email, { bucket, activeVersion } = {}) => {
  if (!email) {
    console.error('setRedesignProfile: email is required');
    return null;
  }

  try {
    const properties = {};
    if (bucket) properties.redesign_bucket = bucket;
    if (activeVersion) properties.redesign_active_version = activeVersion;

    const { data } = await axios.post(`${BASE_API_URL}/api/klaviyo/profile`, {
      email,
      properties,
    });
    return data;
  } catch (error) {
    console.error('Error updating Klaviyo redesign profile:', error);
    return null;
  }
};

/**
 * Redesign A/B assignment, owned by the API (test_assignments). The server
 * assigns on first sight rather than waiting for a login, and puts new members
 * into whichever arm is smaller.
 *
 * @param {string} email
 * @param {{signupDate?: string, knownVersion?: string}} [hints] – signupDate
 *   (Recharge customer.created_at) decides control vs test on first sight;
 *   knownVersion is the version already on their Klaviyo profile, so a member
 *   assigned before the table existed isn't re-rolled.
 * @returns {Promise<{bucket: string, assignedVersion: string, activeVersion: string,
 *   created: boolean}|null>} – `created` is true only when this call is what put
 *   the member in the test, so callers can fire one-time tracking off it.
 */
export const getRedesignAssignment = async (email, { signupDate, knownVersion } = {}) => {
  if (!email) return null;
  try {
    const { data } = await axios.get(
      `${BASE_API_URL}/api/redesign/assignment/${encodeURIComponent(email)}`,
      { params: { ...(signupDate ? { signupDate } : {}), ...(knownVersion ? { knownVersion } : {}) } }
    );
    if (!data?.success) return null;
    return {
      bucket: data.bucket,
      assignedVersion: data.assignedVersion,
      activeVersion: data.activeVersion,
      // 'invite' | 'holdout' for existing members once the wider rollout has
      // started (the API decides when), otherwise null.
      rolloutArm: data.rolloutArm || null,
      created: !!data.created,
    };
  } catch (error) {
    console.error('Error fetching redesign assignment:', error);
    return null;
  }
};

/**
 * Persist a design switch server-side. Moves active_version only — the arm the
 * member was assigned to stays put, so switching never skews the balancer.
 */
export const setRedesignAssignmentVersion = async (email, activeVersion, source) => {
  if (!email || !activeVersion) return null;
  try {
    const { data } = await axios.post(`${BASE_API_URL}/api/redesign/assignment/version`, {
      email,
      activeVersion,
      ...(source ? { source } : {}),
    });
    return data;
  } catch (error) {
    console.error('Error recording redesign version switch:', error);
    return null;
  }
};

/**
 * Fire once, when a member is first bucketed into the experiment.
 * @param {string} email
 * @param {{bucket: string, active_version: string, reason: string, assigned_at: string}} params
 */
export const trackRedesignAssigned = async (email, params = {}) => {
  if (!email) {
    console.error('trackRedesignAssigned: email is required');
    return null;
  }

  try {
    const { data } = await axios.post(`${BASE_API_URL}/api/klaviyo`, {
      emails: [email],
      event: 'Redesign Assigned',
      params,
    });
    return data;
  } catch (error) {
    console.error('Error sending Klaviyo Redesign Assigned event:', error);
    return null;
  }
};

/**
 * Fire every time a member toggles between the old and new experience.
 * @param {string} email
 * @param {{bucket: string, from: string, to: string, switched_at: string}} params
 */
export const trackRedesignSwitch = async (email, params = {}) => {
  if (!email) {
    console.error('trackRedesignSwitch: email is required');
    return null;
  }

  try {
    const { data } = await axios.post(`${BASE_API_URL}/api/klaviyo`, {
      emails: [email],
      event: 'Redesign Version Switched',
      params,
    });
    return data;
  } catch (error) {
    console.error('Error sending Klaviyo Redesign Version Switched event:', error);
    return null;
  }
};

// -----------------------------------------------------------------------------
// Beam – Find nonprofit for a subscription
// -----------------------------------------------------------------------------
export const findNonprofitForSubscription = async (subscriptionId, email) => {
  // Retrieve Beam API key from environment
  const BEAM_API_KEY = process.env.REACT_APP_BEAM_API_KEY;
  if (!BEAM_API_KEY) {
    console.error('Missing REACT_APP_BEAM_API_KEY – cannot fetch nonprofit info');
    return null;
  }

  if (!subscriptionId || !email) return null;

  try {
    // Updated endpoint per Beam guidance (July 2025)
    const url = 'https://api.beamimpact.com/api/v3/subscriptions/findOrCreateSubscription';
    const payload = {
      remoteSubscriptionId: subscriptionId.toString(),
      email,
    };

    const response = await axios.post(url, payload, {
      headers: {
        Authorization: `Api-Key ${BEAM_API_KEY}`,
      },
    });

    const data = response?.data;
    if (!data) return null;

    /*
      Expected response shape:
      {
        remoteSubscriptionId: string,
        nonprofit: {
          id: number,
          name: string
        } | null,
        ...
      }
    */
    const nonprofit = data.nonprofit;

    if (!nonprofit) return null;

    return {
      id: nonprofit.id ?? null,
      name: nonprofit.name ?? null,
      cause: nonprofit.cause ?? null, // `cause` may not be present but we preserve existing contract
    };
  } catch (error) {
    console.error('Error fetching nonprofit for subscription:', error);
    return null;
  }
};

export const submitFeedback = async (type, data = {}, images = []) => {
  if (!type) throw new Error('feedback type is required');
  const url = `${BASE_API_URL}/api/feedback/${type}`;

  const formData = new FormData();

  // Append primitive & complex fields
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value) || typeof value === 'object') {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, value);
    }
  });

  // Append images
  (images || []).forEach((img) => {
    const file = img instanceof File ? img : img?.file;
    if (file) formData.append('images', file);
  });

  try {
    const { data: res } = await axios.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res;
  } catch (error) {
    console.error(`Error submitting ${type} feedback:`, error);
    throw error;
  }
};

export const fetchFeedbacksByUser = async (type, userId) => {
  if (!type) throw new Error('feedback type is required');
  if (!userId) throw new Error('userId is required');
  const url = `${BASE_API_URL}/api/feedback/${type}?userId=${encodeURIComponent(userId)}`;
  try {
    const { data } = await axios.get(url);
    return Array.isArray(data.feedbacks) ? data.feedbacks : [];
  } catch (error) {
    console.error(`Error fetching ${type} feedbacks:`, error);
    return [];
  }
};

// `source` tags which placement drove the share (referral_page, rewards_page,
// post_delivery_rating, feedback_5_star, …) so placements can be compared.
export const incrementLinkShares = async (advocate_id, source = '') => {
  if (!advocate_id) throw new Error('advocate_id is required');
  try {
    const url = `${BASE_API_URL}/api/referrals/advocates/link-shares`;
    const payload = { advocate_id };
    if (source) payload.source = source;
    const { data } = await axios.post(url, payload);
    return data;
  } catch (error) {
    console.error('Error incrementing link shares:', error);
    throw error;
  }
};

// ---------------------------
// Plan Visibility Settings (for Custom Box plans)
// ---------------------------

// Cache for plan visibility data
let planVisibilityCache = {
  data: null,
  timestamp: null,
  ttl: 5 * 60 * 1000, // 5 minutes cache TTL
};

/**
 * Fetch plan visibility settings from the backend.
 * Results are cached for 5 minutes to reduce API calls.
 * 
 * @param {boolean} forceRefresh - If true, bypass the cache and fetch fresh data
 * @returns {Promise<Array<{variant_id: string, is_active_portal: boolean, is_active_pdp: boolean}> | null>}
 */
export const getPlanVisibility = async (forceRefresh = false) => {
  const now = Date.now();

  // Return cached data if valid and not forcing refresh
  if (
    !forceRefresh &&
    planVisibilityCache.data &&
    planVisibilityCache.timestamp &&
    now - planVisibilityCache.timestamp < planVisibilityCache.ttl
  ) {
    return planVisibilityCache.data;
  }

  try {
    const url = `${BASE_API_URL}/api/product/plan-visibility`;
    const response = await axios.get(url);

    // Extract visibility array from response (API returns { success: true, visibility: [...] })
    const visibilityData = response.data?.visibility || response.data;

    // Cache the response
    planVisibilityCache = {
      data: visibilityData,
      timestamp: now,
      ttl: planVisibilityCache.ttl,
    };

    return visibilityData;
  } catch (error) {
    console.error('Error fetching plan visibility settings:', error);
    // Return cached data as fallback if available, even if stale
    if (planVisibilityCache.data) {
      console.warn('Using stale cached plan visibility data as fallback');
      return planVisibilityCache.data;
    }
    // Return null to signal caller should use fallback logic
    return null;
  }
};

/**
 * Check if a specific variant should be visible in the portal.
 * Returns true if visible, false if hidden.
 * If visibility data is unavailable, returns null to indicate fallback logic should be used.
 * 
 * @param {Array} visibilitySettings - The plan visibility settings array
 * @param {string} variantId - The Shopify variant ID to check
 * @returns {boolean | null} - true if visible, false if hidden, null if unknown
 */
export const isVariantVisibleInPortal = (visibilitySettings, variantId) => {
  if (!visibilitySettings || !Array.isArray(visibilitySettings) || !variantId) {
    return null; // Unknown - use fallback logic
  }

  // Normalize variantId for comparison (handle gid:// format and plain numbers)
  const normalizeVariantId = (id) => {
    if (!id) return '';
    const idStr = String(id);
    // Extract numeric ID from gid://shopify/ProductVariant/123456 format
    const match = idStr.match(/(\d+)$/);
    return match ? match[1] : idStr;
  };

  const normalizedTargetId = normalizeVariantId(variantId);

  const visibilityEntry = visibilitySettings.find(entry => {
    const normalizedEntryId = normalizeVariantId(entry.variant_id);
    return normalizedEntryId === normalizedTargetId;
  });

  if (!visibilityEntry) {
    return null; // Not found in settings - use fallback logic
  }

  return visibilityEntry.is_active_portal === true;
};

/**
 * Check if a plan should be visible in the portal by matching size and plan name.
 * This is more reliable than matching by variant ID since the plans Map may not
 * have the correct variant ID for the currently selected size.
 * 
 * @param {Array} visibilitySettings - The plan visibility settings array
 * @param {string} sizeName - The size name (e.g., "4 items", "6 items (most popular)")
 * @param {string} planName - The plan name (e.g., "Italian Box", "Variety Box")
 * @returns {boolean | null} - true if visible, false if hidden, null if unknown
 */
export const isPlanVisibleInPortal = (visibilitySettings, sizeName, planName) => {
  if (!visibilitySettings || !Array.isArray(visibilitySettings) || !sizeName || !planName) {
    return null; // Unknown - use fallback logic
  }

  // Normalize size name for matching (remove parenthetical descriptions for comparison)
  const normalizeSize = (size) => {
    if (!size) return '';
    return size.toLowerCase().replace(/\s*\(.*?\)\s*/g, '').trim();
  };

  const normalizedTargetSize = normalizeSize(sizeName);
  const normalizedTargetPlan = planName.toLowerCase().trim();

  // Find visibility entry by matching variant_title pattern: "SIZE / PLAN | $PRICE"
  const visibilityEntry = visibilitySettings.find(entry => {
    if (!entry.variant_title) return false;
    
    const titleLower = entry.variant_title.toLowerCase();
    
    // Extract size from variant_title (before the /)
    const sizePart = titleLower.split('/')[0]?.trim() || '';
    const normalizedEntrySize = normalizeSize(sizePart);
    
    // Extract plan from variant_title (after / and before |)
    const afterSlash = titleLower.split('/')[1] || '';
    const planPart = afterSlash.split('|')[0]?.trim() || '';
    
    return normalizedEntrySize === normalizedTargetSize && planPart === normalizedTargetPlan;
  });

  if (!visibilityEntry) {
    return null; // Not found in settings - use fallback logic
  }

  return visibilityEntry.is_active_portal === true;
};

// ---------------------------
// Customer Purchased SKUs
// ---------------------------

/**
 * Fetch the list of SKUs that a customer has purchased before.
 * Used to determine which products can be rated.
 * 
 * @param {string} customerId - The customer's Recharge ID
 * @returns {Promise<string[]>} - Array of purchased SKU strings
 */
export const getCustomerPurchasedSkus = async (customerId) => {
  if (!customerId) {
    console.warn('getCustomerPurchasedSkus: customerId is required');
    return [];
  }

  try {
    const url = `${BASE_API_URL}/api/order/customer/${customerId}/skus`;
    const response = await axios.get(url);
    // API returns { customerId, skus: [...] } or possibly just an array
    const data = response.data;
    if (Array.isArray(data)) {
      return data;
    }
    if (data && Array.isArray(data.skus)) {
      return data.skus;
    }
    return [];
  } catch (error) {
    console.error('Error fetching customer purchased SKUs:', error);
    return [];
  }
};

export const disableAutoCharge = async (customerId) => {
  if (!customerId) {
    throw new Error('customerId is required');
  }

  try {
    const url = `${BASE_API_URL}/api/retention/auto-charge/disable`;
    const response = await axios.post(url, { customerId });
    return response.data;
  } catch (error) {
    console.error('Error disabling auto-charge:', error);
    throw error;
  }
};

export const enableAutoCharge = async (customerId, nextChargeDate = null) => {
  if (!customerId) {
    throw new Error('customerId is required');
  }

  try {
    const url = `${BASE_API_URL}/api/retention/auto-charge/enable`;
    const payload = { customerId };
    if (nextChargeDate) {
      payload.nextChargeDate = nextChargeDate;
    }
    const response = await axios.post(url, payload);
    return response.data;
  } catch (error) {
    console.error('Error enabling auto-charge:', error);
    throw error;
  }
};

export const getAutoChargeStatus = async (customerId) => {
  if (!customerId) {
    throw new Error('customerId is required');
  }

  try {
    const url = `${BASE_API_URL}/api/retention/auto-charge/status/${customerId}`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error checking auto-charge status:', error);
    // Return default status on error to not block UI
    return { customerId, autoChargeDisabled: false };
  }
};

// ---------------------------
// AI Product Recommendations
// ---------------------------

// Member-facing AI recommendations (from the cached taste-profile). Never throws;
// an empty result just means nothing to show.
export const getMemberRecommendations = async (email) => {
  if (!email) return { recommendations: [], substitutes: [] };
  try {
    const url = `${BASE_API_URL}/api/users/member/${encodeURIComponent(email)}/recommendations`;
    const response = await axios.get(url);
    return response.data || { recommendations: [], substitutes: [] };
  } catch (error) {
    console.error('Error fetching member recommendations:', error);
    return { recommendations: [], substitutes: [] };
  }
};

// ---------------------------
// Account email change
// ---------------------------

// Member-initiated account email change (Account page). Identity comes from the
// X-Member-Token the interceptor attaches; the API updates Shopify + Recharge.
// Throws on failure — callers surface error.response.data.error (e.g. "That
// email is already in use on another account.") to the member.
export const changeAccountEmail = async (newEmail) => {
  const url = `${BASE_API_URL}/api/users/member/change-email`;
  const { data } = await axios.post(url, { email: newEmail });
  return data;
};

// ---------------------------
// Top Rated Products
// ---------------------------

// Netflix-style "Top 10" chart — best-rated products over a recent window,
// ranked server-side. `minReviews` sets the review floor a product needs to
// chart (the API defaults to 5; short windows need a lower floor). Never
// throws; an empty result just means the section doesn't render. We
// over-fetch beyond 10 so the section can re-rank after filtering to what
// the member can actually add.
export const getTopRatedProducts = async ({ days = 90, limit = 20, minReviews } = {}) => {
  try {
    const url = `${BASE_API_URL}/api/feedback/public/product/top-rated?days=${days}&limit=${limit}${minReviews ? `&minReviews=${minReviews}` : ''}`;
    const response = await axios.get(url);
    return response.data?.products || [];
  } catch (error) {
    console.error('Error fetching top rated products:', error);
    return [];
  }
};

// ---------------------------
// Portal Error Logging
// ---------------------------

export const logPortalError = async (message, error, context = {}) => {
  try {
    const url = `${BASE_API_URL}/api/portal-log`;
    
    // Serialize the error object
    const errorDetails = error instanceof Error
      ? {
          name: error.name,
          message: error.message,
          stack: error.stack,
          ...(error.response && {
            responseStatus: error.response.status,
            responseData: error.response.data,
            responseHeaders: error.response.headers,
          }),
        }
      : error;
    
    await axios.post(url, {
      message,
      error: errorDetails,
      context,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    });
  } catch (logError) {
    // Don't throw if logging fails - just log to console
    console.error('Failed to log error to backend:', logError);
  }
};

// ---------------------------
// Free Promo (Free Item for Life)
// ---------------------------

/**
 * Enable the FREE_PROMO for a subscription.
 * This adds a property to the subscription that tells newOrderCreation.js
 * to add a free item to every box for this subscription.
 * 
 * @param {string} subscriptionId - The Recharge subscription ID
 * @param {string} cancellationReason - The reason for cancellation (for logging)
 * @returns {Promise<Object>} Response with success status and free item SKU
 */
export const enableFreePromo = async (subscriptionId, cancellationReason = '') => {
  if (!subscriptionId) {
    throw new Error('subscriptionId is required');
  }

  try {
    const url = `${BASE_API_URL}/api/retention/free-promo/enable`;
    const response = await axios.post(url, { subscriptionId, cancellationReason });
    return response.data;
  } catch (error) {
    console.error('Error enabling free promo:', error);
    throw error;
  }
};

/**
 * Win-back free item (butter in every box) for the reactivate_free_item magic
 * link. Must be called while the subscription is still CANCELLED: the API sets
 * PROMO_SWAP to the campaign SKU (fixed server-side) and refuses active subs,
 * Plant-Based / Protein plans and 4-item plans (422 with a `reason`).
 * Resolves `{ alreadyApplied }` when the swap was already set (second click).
 *
 * @param {string} subscriptionId - The Recharge subscription ID
 */
/**
 * Box 1 check-in answer ("How's your first box going?") from the portal's
 * BoxProgressModal — the HTML version of the email links there with the
 * member's answer. Same public endpoint and Klaviyo "First Box Progress" event
 * as the email forms; `source` tells them apart.
 */
export const submitBoxProgress = async (email, status) => {
  const url = `${BASE_API_URL}/api/box-progress`;
  const response = await axios.post(url, { email, status, source: 'portal_modal', form: 'box-progress' });
  return response.data;
};

export const enableWinbackFreeItem = async (subscriptionId) => {
  if (!subscriptionId) {
    throw new Error('subscriptionId is required');
  }

  const url = `${BASE_API_URL}/api/retention/winback-free-item`;
  const response = await axios.post(url, { subscriptionId });
  return response.data;
};

/**
 * Campaign free item (Carbsgiving 2026: free Classic Apple Pie) for the
 * `carbsgiving_apple_pie` magic link. The subscription must be ACTIVE and a
 * box plan: the API adds the campaign product as a $0 onetime on its address
 * (product and price fixed server-side) and refuses GF / Plant-Based plans
 * (422 with a `reason`). Resolves `{ alreadyApplied }` on a second click.
 *
 * @param {string} subscriptionId - The Recharge subscription ID
 * @param {string} campaign - Campaign key, e.g. 'carbsgiving_apple_pie'
 */
export const addCampaignFreeItem = async (subscriptionId, campaign) => {
  if (!subscriptionId) {
    throw new Error('subscriptionId is required');
  }

  const url = `${BASE_API_URL}/api/retention/campaign-free-item`;
  const response = await axios.post(url, { subscriptionId, campaign });
  return response.data;
};

/**
 * Check if a subscription has FREE_PROMO enabled.
 * The actual free item SKU is determined at order time based on the plan type:
 * - Regular plans (Variety, Italian, Bread, Pastry): Croissant
 * - Plant-Based: Vegan Croissant  
 * - Gluten-Free: GF Chocolate Chip Cookie
 * - Protein: Plain Biscuits (WG_HPPB_GA, swapped from the High Protein Maple Waffle while OOS)
 * 
 * @param {string} subscriptionId - The Recharge subscription ID
 * @returns {Promise<Object>} Response with freePromoEnabled status
 */
export const getFreePromoStatus = async (subscriptionId) => {
  if (!subscriptionId) {
    throw new Error('subscriptionId is required');
  }

  try {
    const url = `${BASE_API_URL}/api/retention/free-promo/status/${subscriptionId}`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error checking free promo status:', error);
    // Return default status on error to not block UI
    return { subscriptionId, freePromoEnabled: false };
  }
};

// ---------------------------
// Switch your free item (PROMO_SWAP from a per-plan menu)
// ---------------------------

/**
 * Menu of free items the member can switch to, minus the one they have today.
 * Resolves `{ eligible, reason, current, options, planKey, is4ItemPlan }`;
 * `eligible: false` (Protein Box, 4-item plan without the free-item-for-life
 * perk, cancelled sub) means the offer should simply be hidden. Never throws:
 * a failed lookup resolves ineligible so no retention screen breaks on it.
 *
 * @param {string} subscriptionId - The Recharge subscription ID
 */
export const getPromoSwitchOptions = async (subscriptionId) => {
  if (!subscriptionId) return { eligible: false, reason: 'missing_subscription', current: null, options: [] };
  try {
    const url = `${BASE_API_URL}/api/retention/promo-switch/options/${subscriptionId}`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error loading free item options:', error);
    return { eligible: false, reason: 'request_failed', current: null, options: [] };
  }
};

/**
 * Switch the free item that ships in every box. The API sets PROMO_SWAP on the
 * active box subscription; the SKU must be one of the options returned above
 * (422 with a `reason` otherwise). Resolves `{ item, alreadyApplied, previousSku }`.
 *
 * @param {string} subscriptionId - The Recharge subscription ID
 * @param {string} sku - One of the option SKUs
 * @param {Object} [meta] - { source: 'cancel_flow' | 'manage_plan', cancellationReason }
 */
export const switchPromoItem = async (subscriptionId, sku, { source = 'member_portal', cancellationReason = null } = {}) => {
  if (!subscriptionId) {
    throw new Error('subscriptionId is required');
  }
  if (!sku) {
    throw new Error('sku is required');
  }

  const url = `${BASE_API_URL}/api/retention/promo-switch`;
  const response = await axios.post(url, { subscriptionId, sku, source, cancellationReason });
  return response.data;
};

// ---------------------------
// Fallback Items (Out-of-Stock Substitution Preferences)
// ---------------------------

/**
 * Get fallback items for a subscription.
 * Fallback items are used for out-of-stock substitutions.
 * When an item is out of stock, the system will try these fallback items
 * in order before falling back to automatic selection.
 * 
 * @param {string} subscriptionId - The Recharge subscription ID
 * @returns {Promise<Array<{sku: string, name: string}>>} Array of fallback items
 */
export const getFallbackItems = async (subscriptionId) => {
  if (!subscriptionId) {
    throw new Error('subscriptionId is required');
  }

  try {
    const url = `${BASE_API_URL}/api/recharge/subscriptions/${subscriptionId}/fallbacks`;
    const response = await axios.get(url);
    return response.data?.fallback_items || [];
  } catch (error) {
    console.error('Error fetching fallback items:', error);
    return [];
  }
};

/**
 * Update fallback items for a subscription.
 * These items will be used as substitutes when an item in the order is out of stock.
 * 3-5 fallback items required, tried in priority order.
 * 
 * @param {string} subscriptionId - The Recharge subscription ID
 * @param {Array<{sku: string, name: string}>} fallbackItems - Array of fallback items (3-5)
 * @returns {Promise<Object>} Response with success status
 */
export const updateFallbackItems = async (subscriptionId, fallbackItems) => {
  if (!subscriptionId) {
    throw new Error('subscriptionId is required');
  }

  if (!Array.isArray(fallbackItems)) {
    throw new Error('fallbackItems must be an array');
  }

  if (fallbackItems.length > 0 && fallbackItems.length < 3) {
    throw new Error('Minimum 3 fallback items required');
  }

  if (fallbackItems.length > 5) {
    throw new Error('Maximum 5 fallback items allowed');
  }

  try {
    const url = `${BASE_API_URL}/api/recharge/subscriptions/${subscriptionId}/fallbacks`;
    const response = await axios.put(url, { fallback_items: fallbackItems });
    return response.data;
  } catch (error) {
    console.error('Error updating fallback items:', error);
    throw error;
  }
};

export const addCredits = async (customerId) => {
  if (!customerId) {
    throw new Error('customerId is required');
  }

  try {
    const url = `${BASE_API_URL}/api/recharge/add-credits`;
    const response = await axios.post(url, { customerId });
    return response.data;
  } catch (error) {
    console.error('Error adding credits:', error);
    throw error;
  }
};

export const addPromoCredits = async (customerId) => {
  if (!customerId) {
    throw new Error('customerId is required');
  }

  try {
    const url = `${BASE_API_URL}/api/recharge/add-promo-credits`;
    const response = await axios.post(url, { customerId });
    return response.data;
  } catch (error) {
    console.error('Error adding promo credits:', error);
    throw error;
  }
};

export const addReminderCredits = async (customerId, emailDate = null, amount = null) => {
  if (!customerId) {
    throw new Error('customerId is required');
  }

  try {
    const url = `${BASE_API_URL}/api/recharge/add-reminder-credits`;
    const payload = { customerId };
    if (emailDate) payload.emailDate = emailDate;
    if (amount) payload.amount = amount;
    const response = await axios.post(url, payload);
    return response.data;
  } catch (error) {
    console.error('Error adding reminder credits:', error);
    throw error;
  }
};

export const addSmsCredits = async (customerId) => {
  if (!customerId) {
    throw new Error('customerId is required');
  }

  try {
    const url = `${BASE_API_URL}/api/recharge/add-sms-credits`;
    const response = await axios.post(url, { customerId });
    return response.data;
  } catch (error) {
    console.error('Error adding SMS credits:', error);
    throw error;
  }
};

// ---------------------------
// Wildgrain Wallet (Apple / Google Wallet passes)
// ---------------------------

/**
 * Generate an Apple Wallet pass (.pkpass) for a customer.
 * The backend handles upsert — calling this for an existing customer
 * returns/regenerates their existing pass (same serial number) with
 * fresh subscription/credit data.
 *
 * @param {Object} payload
 * @param {string|number} [payload.customerId] - Recharge customer ID
 * @param {string} payload.email
 * @param {string} payload.firstName
 * @param {string} payload.lastName
 * @param {string} [payload.memberSince] - ISO date string
 * @param {string} [payload.nextDeliveryDate] - ISO date string
 * @returns {Promise<Blob>} The .pkpass file as a Blob (application/vnd.apple.pkpass)
 */
export const generateAppleWalletPass = async ({
  customerId,
  email,
  firstName,
  lastName,
  memberSince,
  nextDeliveryDate,
} = {}) => {
  if (!email || !firstName || !lastName) {
    throw new Error('email, firstName, and lastName are required');
  }

  try {
    const url = `${BASE_API_URL}/api/wallet/pass`;
    const response = await axios.post(
      url,
      { customerId, email, firstName, lastName, memberSince, nextDeliveryDate },
      { responseType: 'blob' }
    );
    return response.data;
  } catch (error) {
    console.error('Error generating Apple Wallet pass:', error);
    throw error;
  }
};

/**
 * Generate a Google Wallet pass save URL for a customer.
 * The backend handles upsert — calling this for an existing customer
 * returns the save URL for their existing pass with fresh data.
 *
 * @param {Object} payload - Same shape as generateAppleWalletPass
 * @returns {Promise<{saveUrl: string, token: string}>}
 */
export const generateGoogleWalletPass = async ({
  customerId,
  email,
  firstName,
  lastName,
  memberSince,
  nextDeliveryDate,
} = {}) => {
  if (!email || !firstName || !lastName) {
    throw new Error('email, firstName, and lastName are required');
  }

  try {
    const url = `${BASE_API_URL}/api/wallet/google-pass`;
    const { data } = await axios.post(url, {
      customerId,
      email,
      firstName,
      lastName,
      memberSince,
      nextDeliveryDate,
    });
    return { saveUrl: data?.saveUrl, token: data?.token };
  } catch (error) {
    console.error('Error generating Google Wallet pass:', error);
    throw error;
  }
};

// ---------------------------
// Address autocomplete (Geoapify)
// ---------------------------

// Geoapify's Address Autocomplete API backs the street-address typeahead on
// every address form in the portal (shipping, gift recipient, gift
// redemption, billing). Picked because it is genuinely self-serve: email
// signup, no card, key issued instantly, 3,000 requests/day free with
// commercial use allowed. The free plan asks for a "Powered by Geoapify"
// link near the field, which AddressAutocompleteInput renders.
//
// The key is optional: without REACT_APP_GEOAPIFY_API_KEY the forms silently
// fall back to the browser's native autofill (autocomplete attrs). Lock the
// key to the portal's domains under "HTTP referrers" in the Geoapify
// project settings, since it ships in the bundle.
const GEOAPIFY_AUTOCOMPLETE_URL = 'https://api.geoapify.com/v1/geocode/autocomplete';
const GEOAPIFY_API_KEY = process.env.REACT_APP_GEOAPIFY_API_KEY || '';

export const ADDRESS_AUTOCOMPLETE_ATTRIBUTION = {
  label: 'Powered by Geoapify',
  url: 'https://www.geoapify.com/',
};

export const isAddressAutocompleteEnabled = () => Boolean(GEOAPIFY_API_KEY);

// Geoapify reports the municipality ("Town of Southold", "City of
// Watervliet"); carriers want the plain place name.
const cleanCityName = (city) => (city || '').replace(/^(town|city|village|township|borough) of\s+/i, '').trim();

// Map a Geoapify result onto the Recharge address shape our forms use.
// `province` is the full state name to match what Shopify checkout writes
// into Recharge; `province_code` is there for forms that use a code select.
// Street-level matches (no house number yet) keep the number the member
// already typed so picking one doesn't throw it away.
const normalizeGeoapifyAddress = (result, query) => {
  if (!result?.street) return null;
  const typedNumber = query.match(/^\s*(\d+[a-z]?)\b/i)?.[1] || '';
  const address1 = [result.housenumber || typedNumber, result.street].filter(Boolean).join(' ');
  return {
    address1,
    address2: '',
    city: cleanCityName(result.city || result.town || result.village || result.county),
    province: result.state || result.state_code || '',
    province_code: result.state_code || getStateAbbreviation(result.state || ''),
    zip: result.postcode || '',
    country_code: (result.country_code || 'us').toUpperCase(),
    label: result.formatted || address1,
  };
};

/**
 * Look up US street-address suggestions for a partial query.
 * Never throws for an aborted request (the caller cancels stale keystrokes);
 * any other failure is logged and resolves to [] so the form stays usable.
 *
 * @param {string} query - What the member has typed so far (min 3 chars)
 * @param {{ signal?: AbortSignal, limit?: number }} [options]
 * @returns {Promise<Array<{address1, address2, city, province, province_code, zip, country_code, label}>>}
 */
export const searchAddressSuggestions = async (query, { signal, limit = 5 } = {}) => {
  const trimmed = (query || '').trim();
  if (!GEOAPIFY_API_KEY || trimmed.length < 3) return [];

  try {
    const { data } = await axios.get(GEOAPIFY_AUTOCOMPLETE_URL, {
      params: {
        text: trimmed,
        filter: 'countrycode:us',
        lang: 'en',
        format: 'json',
        limit,
        apiKey: GEOAPIFY_API_KEY,
      },
      signal,
    });
    // Only street-or-better matches are useful for a shipping form; drop
    // city / postcode / state-level hits and de-dupe identical labels.
    const seen = new Set();
    return (data?.results || [])
      .filter((r) => ['building', 'amenity', 'street'].includes(r.result_type))
      .map((r) => normalizeGeoapifyAddress(r, trimmed))
      .filter((s) => s && !seen.has(s.label) && seen.add(s.label));
  } catch (error) {
    if (axios.isCancel(error)) return [];
    console.warn('Address autocomplete lookup failed:', error?.response?.data || error.message);
    return [];
  }
};

// Held-order substitution (out-of-stock item swapped for a replacement).
// Members land here from the "Substitute" link on the storefront order page.
export const getOrderSubstitution = async (orderName) => {
  const { data } = await axios.get(
    `${BASE_API_URL}/api/order/portal/substitution/${encodeURIComponent(orderName)}`
  );
  return data;
};

// The member's held orders with a substitution they can still change. Cached
// briefly because both Overview and the box view ask on every load.
export const getSubstitutionHolds = async (shopifyCustomerId) => {
  if (!shopifyCustomerId) return [];
  return cachedFetch(['substitutionHolds', String(shopifyCustomerId)], async () => {
    const { data } = await axios.get(
      `${BASE_API_URL}/api/order/portal/substitution-holds/${encodeURIComponent(shopifyCustomerId)}`
    );
    return data?.holds || [];
  }, 2 * 60 * 1000);
};

export const substituteOrderItem = async (orderName, sku, newSku) => {
  const { data } = await axios.put(
    `${BASE_API_URL}/api/order/portal/substitution/${encodeURIComponent(orderName)}`,
    { sku, newSku }
  );
  return data;
};
